"use client";

import { safeRedirect } from "./auth-redirects";
import { landingPath, UTM_FIELDS, type UtmField } from "./campaign-visit";

const STORAGE_KEY = "tradesfolk_signup_attribution_v1";

type AttributionTouch = Partial<Record<UtmField, string>> & {
  visit_id?: string;
  landing_path: string;
  captured_at: string;
};

type StoredAttribution = {
  first: AttributionTouch;
  last: AttributionTouch;
};

function clean(value: string | null, maxLength: number): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, maxLength);
}

function readStoredAttribution(): StoredAttribution | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredAttribution>;
    if (
      !parsed.first ||
      !parsed.last ||
      typeof parsed.first.landing_path !== "string" ||
      typeof parsed.last.landing_path !== "string" ||
      typeof parsed.first.captured_at !== "string" ||
      typeof parsed.last.captured_at !== "string"
    ) {
      return null;
    }

    return parsed as StoredAttribution;
  } catch {
    return null;
  }
}

// Retry failed requests on the next navigation; duplicate IDs are ignored by the
// server. Session storage keeps the same touch across reloads and auth handoffs.
const sentVisits = new Set<string>();
function recordVisit(touch: AttributionTouch) {
  if (!touch.visit_id || sentVisits.has(touch.visit_id)) return;
  const id = touch.visit_id;
  sentVisits.add(id);
  void fetch("/api/campaign-visit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(touch),
    keepalive: true,
  }).then((response) => { if (!response.ok) sentVisits.delete(id); })
    .catch(() => { sentVisits.delete(id); });
}

export function captureSignupAttribution() {
  try {
    let params = new URLSearchParams(window.location.search);
    let path = window.location.pathname;
    // The proxy sends anonymous protected-route visits to login with the full
    // destination in next. Capture that tagged landing before signup begins.
    if (!UTM_FIELDS.some((field) => params.get(field))) {
      const next = safeRedirect(params.get("next"), "");
      if (next) {
        const target = new URL(next, window.location.origin);
        params = target.searchParams;
        path = target.pathname;
      }
    }
    const touch: Partial<AttributionTouch> = {};

    for (const field of UTM_FIELDS) {
      const maxLength =
        field === "utm_source" || field === "utm_medium" ? 100 : 200;
      const value = clean(params.get(field), maxLength);
      if (value) touch[field] = value;
    }

    const current = readStoredAttribution();
    if (!UTM_FIELDS.some((field) => touch[field])) {
      if (current) recordVisit(current.last);
      return;
    }

    touch.landing_path = landingPath(path);
    touch.captured_at = new Date().toISOString();

    if (current?.last.visit_id && current.last.landing_path === touch.landing_path &&
      UTM_FIELDS.every((field) => current.last[field] === touch[field])) {
      recordVisit(current.last);
      return;
    }
    touch.visit_id = crypto.randomUUID();
    const next: StoredAttribution = {
      first: current?.first ?? (touch as AttributionTouch),
      last: touch as AttributionTouch,
    };

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    recordVisit(next.last);
  } catch {
    // Attribution must never interfere with browsing or signup.
  }
}

export function appendSignupAttribution(formData: FormData) {
  captureSignupAttribution();
  const stored = readStoredAttribution();
  if (!stored) return;

  for (const [position, touch] of [
    ["first", stored.first],
    ["last", stored.last],
  ] as const) {
    if (touch.visit_id) formData.set(`attribution_${position}_visit_id`, touch.visit_id);
    for (const field of UTM_FIELDS) {
      const value = touch[field];
      if (value) {
        formData.set(`attribution_${position}_${field}`, value);
      }
    }
    formData.set(
      `attribution_${position}_landing_path`,
      touch.landing_path
    );
    formData.set(`attribution_${position}_captured_at`, touch.captured_at);
  }
}
